import Immutable from 'immutable';
import React from 'react';
import { useSelector } from 'react-redux';
import CarouselItem from 'components/CarouselItem';
import CarouselItemSlide from 'components/CarouselItemSlide';
import { CAROUSEL_ITEM_TYPES } from 'containers/Layouts/sagas/addCarousel';
import { sectionsWidthByName } from 'containers/ScreenDefinitions/selectors';
import * as Models from 'models';
import { intlGet } from 'utils/intlGet';
import CarouselTitle from './CarouselTitle';
import styles from './styles.module.scss';

const intlAssetsElement = (type: string): string => intlGet('Section.Carousel.CarouselItemType', type);

type Props = {
  carouselId: string;
  documents?: Models.CombinedDocumentsMap;
  getLayoutsRefs?: () => Immutable.Map<string, HTMLDivElement>;
  isSectionActive: boolean;
  isReadOnly: boolean;
  layoutIds: Immutable.List<string>;
  layouts: Models.CombinedLayoutsList;
  moveableLayoutId?: string;
  relationsByRegularLayoutId?: Immutable.Map<string, Models.LayeredRelationsMap>;
  saveLayoutRef?: (layoutId: string, element: HTMLDivElement) => void;
  section: Models.ExtendedSectionMap;
  sectionDisplayName: string;
  getLayoutDragHotspotPosition: (clientY: number) => void;
};

export const CAROUSEL_ITEM_TYPE_HINT_TEXT = {
  [CAROUSEL_ITEM_TYPES.SLIDE]: intlAssetsElement(CAROUSEL_ITEM_TYPES.SLIDE),
  [CAROUSEL_ITEM_TYPES.CUSTOM_TEXT]: intlAssetsElement(CAROUSEL_ITEM_TYPES.CUSTOM_TEXT),
  [CAROUSEL_ITEM_TYPES.CONTROLS]: intlAssetsElement(CAROUSEL_ITEM_TYPES.CONTROLS),
};

const MIN_SLIDES_COUNT = 2;

const Carousel: React.FC<Props> = (props) => {
  const {
    carouselId,
    documents,
    getLayoutsRefs,
    isSectionActive,
    isReadOnly,
    layoutIds,
    layouts,
    moveableLayoutId,
    relationsByRegularLayoutId,
    saveLayoutRef,
    section,
    sectionDisplayName,
    getLayoutDragHotspotPosition,
  } = props;

  const dragOverHandler = (event: React.MouseEvent): void => {
    const { clientY } = event;
    getLayoutDragHotspotPosition(clientY);
  };

  const sectionsWidth = useSelector(sectionsWidthByName);
  const sectionName = section.get('name');
  const sectionWidth = sectionsWidth.get(sectionName);

  const inlineStyle = { '--carousel-width': `${sectionWidth}px` } as React.CSSProperties;

  const slideLayouts = layouts.filter(layout => (layout as Models.LayoutMap).get('carouselItem')?.get('type') === CAROUSEL_ITEM_TYPES.SLIDE);
  const customTextLayouts = layouts.
    filter(layout => (layout as Models.LayoutMap).get('carouselItem')?.get('type') === CAROUSEL_ITEM_TYPES.CUSTOM_TEXT);
  const controlsLayouts = layouts.
    filter(layout => (layout as Models.LayoutMap).get('carouselItem')?.get('type') === CAROUSEL_ITEM_TYPES.CONTROLS);

  // Group slides by slide ID
  const slidesBySlideId = slideLayouts.reduce((acc, layout) => {
    const layoutMap = layout as Models.LayoutMap;
    const carouselItem = layoutMap.get('carouselItem');
    const slideId = carouselItem?.get('slideId');

    if (slideId) {
      if (!acc[slideId]) {
        acc[slideId] = Immutable.List();
      }
      acc[slideId] = acc[slideId].push(layout);
    }

    return acc;
  }, {} as Record<string, Models.CombinedLayoutsList>);

  // Sort slideIds by slideNumber to ensure correct order
  const slideIds = Object.keys(slidesBySlideId).sort((a, b) => {
    const layoutsA = slidesBySlideId[a];
    const layoutsB = slidesBySlideId[b];

    // Get slideNumber from first layout in each slide group
    const slideNumberA = (layoutsA.get(0) as Models.LayoutMap)?.get('carouselItem')?.get('slideNumber') ?? 0;
    const slideNumberB = (layoutsB.get(0) as Models.LayoutMap)?.get('carouselItem')?.get('slideNumber') ?? 0;

    return slideNumberA - slideNumberB;
  });

  const isSlideDeleteDisabled = slideIds.length <= MIN_SLIDES_COUNT;

  return (
    <div className={styles.carousel} style={inlineStyle} onDragOver={dragOverHandler}>
      <CarouselTitle />
      {slideIds.map((slideId, index) => {
        const slideLayoutsInSlide = slidesBySlideId[slideId];

        return (
          <CarouselItemSlide
            type={CAROUSEL_ITEM_TYPES.SLIDE}
            key={slideId}
            carouselId={carouselId}
            documents={documents}
            getLayoutsRefs={getLayoutsRefs}
            isSectionActive={isSectionActive}
            isReadOnly={isReadOnly}
            layoutIds={layoutIds}
            moveableLayoutId={moveableLayoutId}
            relationsByRegularLayoutId={relationsByRegularLayoutId}
            saveLayoutRef={saveLayoutRef}
            section={section}
            slideLayoutsInSlide={slideLayoutsInSlide}
            slideId={slideId}
            number={index + 1}
            sectionDisplayName={sectionDisplayName}
            isSlideDeleteDisabled={isSlideDeleteDisabled}
          />
        );
      })}
      <CarouselItem
        type={CAROUSEL_ITEM_TYPES.CUSTOM_TEXT}
        carouselId={carouselId}
        documents={documents}
        getLayoutsRefs={getLayoutsRefs}
        isSectionActive={isSectionActive}
        isReadOnly={isReadOnly}
        layoutIds={layoutIds}
        moveableLayoutId={moveableLayoutId}
        relationsByRegularLayoutId={relationsByRegularLayoutId}
        saveLayoutRef={saveLayoutRef}
        section={section}
        carouselItemLayouts={customTextLayouts}
        sectionDisplayName={sectionDisplayName}
      />
      <CarouselItem
        type={CAROUSEL_ITEM_TYPES.CONTROLS}
        carouselId={carouselId}
        documents={documents}
        getLayoutsRefs={getLayoutsRefs}
        isSectionActive={isSectionActive}
        isReadOnly={isReadOnly}
        layoutIds={layoutIds}
        moveableLayoutId={moveableLayoutId}
        relationsByRegularLayoutId={relationsByRegularLayoutId}
        saveLayoutRef={saveLayoutRef}
        section={section}
        carouselItemLayouts={controlsLayouts}
        sectionDisplayName={sectionDisplayName}
      />
      <CarouselTitle position='bottom'/>
    </div>
  );
};

export default Carousel;
